import React, { useEffect, useState } from "react";
import client, { apiErr } from "../../lib/api";
import { toast } from "sonner";
import { Copy, RefreshCw, AlertTriangle } from "lucide-react";
import { Drawer, fmtDate } from "../team/shared";

const ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/public/sheet-leads`;

const buildScript = (secret) => `const ENDPOINT = '${ENDPOINT}';
const SECRET = '${secret}';

const MARK = 'CRM Sync';

function post(rows, pending) {
  const res = UrlFetchApp.fetch(ENDPOINT, { method: 'post', contentType: 'application/json',
    payload: JSON.stringify({ secret: SECRET, rows: rows, pending: pending }), muteHttpExceptions: true });
  if (res.getResponseCode() !== 200) { console.log(res.getContentText()); return false; }
  return true;
}

function sendNewLeads() {
  const lock = LockService.getScriptLock();
  if (!lock.tryLock(5000)) return;
  try {
    const sh = SpreadsheetApp.getActiveSpreadsheet().getSheets()[0];
    const lastCol = Math.max(sh.getLastColumn(), 1);
    const headers = sh.getRange(1, 1, 1, lastCol).getValues()[0].map(String);
    let col = headers.indexOf(MARK) + 1;
    if (!col) { col = lastCol + 1; sh.getRange(1, col).setValue(MARK); }
    const width = Math.max(col, lastCol);
    const total = sh.getLastRow();
    const pending = [];
    if (total >= 2) {
      sh.getRange(2, 1, total - 1, width).getValues().forEach(function (r, i) {
        const filled = r.some(function (v, j) { return j !== col - 1 && String(v).trim() !== ''; });
        if (filled && String(r[col - 1]).trim() === '') {
          const o = {}; headers.forEach(function (h, j) { if (h && j !== col - 1) o[h] = String(r[j]); });
          pending.push({ row: i + 2, data: o });
        }
      });
    }
    if (!pending.length) { post([], 0); return; }
    for (let s = 0; s < pending.length; s += 200) {
      const batch = pending.slice(s, s + 200);
      if (!post(batch.map(function (p) { return p.data; }), pending.length - s)) return;
      const stamp = 'synced ' + Utilities.formatDate(new Date(), Session.getScriptTimeZone(), 'yyyy-MM-dd HH:mm');
      sh.getRangeList(batch.map(function (p) { return sh.getRange(p.row, col).getA1Notation(); })).setValue(stamp);
    }
  } finally { lock.releaseLock(); }
}

function setup() {
  ScriptApp.getProjectTriggers().forEach(function (t) { ScriptApp.deleteTrigger(t); });
  ScriptApp.newTrigger('sendNewLeads').timeBased().everyMinutes(1).create();
  sendNewLeads();
}`;

const STEPS = [
  "Google Sheet kholein (fatehfxai@gmail.com wali) → menu me Extensions → Apps Script.",
  "Wahan jo code hai (purana script bhi) use poora hata kar neeche wala naya script paste karein → Save (💾).",
  "Upar function dropdown me 'setup' chunein → Run. Google permission maange to Allow karein.",
  "Bas! Sheet ke end me 'CRM Sync' column ban jayega. Har minute nayi leads CRM me aayengi aur bheji gayi row pe 'synced' likh jayega.",
];

export function SheetSync({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const load = () => client.get("/lead-intake").then(({ data }) => setCfg(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const stale = cfg && (!cfg.last_check_at || Date.now() - new Date(cfg.last_check_at).getTime() > 5 * 60000);
  const copy = () => navigator.clipboard.writeText(buildScript(cfg.secret)).then(() => toast.success("Script copied")).catch(() => toast.error("Copy failed"));
  const regen = async () => {
    if (!window.confirm("New secret banane se purana script kaam karna band kar dega. Continue?")) return;
    try { const { data } = await client.post("/lead-intake/regenerate"); setCfg(data); toast.success("New secret — script dobara paste karein"); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Drawer title="Google Sheet → CRM Sync" onClose={onClose} testid="sheet-sync-drawer">
      {!cfg ? null : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="surface p-3"><div className="font-mono text-xl text-gold" data-testid="sync-total">{cfg.total_created}</div><div className="text-[10px] uppercase text-slate-500">Leads imported</div></div>
            <div className="surface p-3"><div className={`text-sm ${stale ? "text-bear" : "text-bull"}`} data-testid="sync-last-check">{cfg.last_check_at ? fmtDate(cfg.last_check_at) : "Never"}</div><div className="text-[10px] uppercase text-slate-500">Last check</div></div>
            <div className="surface p-3"><div className="text-sm text-white" data-testid="sync-last">{cfg.last_push_at ? fmtDate(cfg.last_push_at) : "—"}</div><div className="text-[10px] uppercase text-slate-500">Last new lead</div></div>
          </div>
          {stale && (
            <div data-testid="sync-stale-warning" className="flex gap-2 items-start border border-[#f59e0b]/40 bg-[#f59e0b]/10 px-3 py-2 text-xs text-amber-100">
              <AlertTriangle size={14} className="text-[#f59e0b] mt-0.5 shrink-0" />
              <span>Sheet se 5 minute se koi check-in nahi aaya. Neeche ka naya script copy karke Apps Script me paste karein aur 'setup' Run karein.</span>
            </div>
          )}
          <ol className="space-y-2 text-sm text-slate-300 list-decimal ps-5">{STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
          <p className="text-[11px] text-slate-500">Leads sheet ke pehle tab me honi chahiye. Columns jaise full_name, email, phone_number aur capital wala sawaal khud pehchane jayenge. Pehli baar purani rows bhi aayengi (duplicate skip).</p>
          <div className="flex gap-2">
            <button data-testid="sync-copy-script" onClick={copy} className="btn-gold flex-1 py-2.5 text-sm flex items-center justify-center gap-2"><Copy size={14} />Copy Script</button>
            <button data-testid="sync-regenerate" onClick={regen} className="px-3 text-xs border border-[color:var(--border-strong)] text-slate-400 hover:text-white flex items-center gap-1.5"><RefreshCw size={13} />New secret</button>
          </div>
          <pre data-testid="sync-script" className="text-[10px] leading-relaxed font-mono text-slate-400 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] p-3 max-h-64 overflow-auto whitespace-pre-wrap">{buildScript(cfg.secret)}</pre>
        </div>
      )}
    </Drawer>
  );
}
